import { useEffect, useRef } from "react";
import type { Image } from "@photography-booking/shared";
import { assetUrl } from "../api/client.js";

interface LightboxProps {
  /** The image to present enlarged. */
  image: Image;
  /** Invoked when the viewer requests to close the enlarged view. */
  onClose: () => void;
}

/**
 * Enlarged image overlay (Req 2.4). Rendered only while a gallery card is
 * selected; presents a single enlarged image with a close affordance. Closes
 * on the close button, an overlay backdrop click, or the Escape key.
 */
export function Lightbox({ image, onClose }: LightboxProps): JSX.Element {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        onClose();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    // Move focus to the close control so keyboard users land inside the dialog.
    closeButtonRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  return (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Enlarged image"
      onClick={onClose}
    >
      <div
        className="lightbox__content"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          ref={closeButtonRef}
          type="button"
          className="lightbox__close"
          aria-label="Close enlarged image"
          onClick={onClose}
        >
          &times;
        </button>
        <img
          className="lightbox__image"
          src={assetUrl(image.renditionKey ?? image.originalKey)}
          alt=""
        />
      </div>
    </div>
  );
}
